import { BillingMonth } from '../types/tampuuri';
import { LOAD_STATES } from './common';
import { KENNO_TENANT_ACTIONS, KENNO_TENANT_EVENTS } from './kennoTenantActions';
import { KennoTenantState, initialState } from './kennoTenantContext';

export function kennoTenantReducer(
  state: KennoTenantState,
  [type, payload]: [KENNO_TENANT_EVENTS | KENNO_TENANT_ACTIONS, any?]
): KennoTenantState {
  switch (type) {
    case KENNO_TENANT_ACTIONS.LOGIN:
      return initialState;
    case KENNO_TENANT_EVENTS.LOGGEDIN:
      return {
        ...state,
        user: {
          isLoggedIn: true,
          isLoaded: true,
          attributes: {
            ...payload,
            // Fallback to true, until this is implemented in the backend
            visited: payload?.visited ? payload.visited : true,
          },
        },
      };

    case KENNO_TENANT_ACTIONS.LOGOUT:
      return { ...initialState, user: { isLoggedIn: false, isLoaded: true } };
    case KENNO_TENANT_EVENTS.LOGGEDOUT:
      return { ...initialState, user: { isLoggedIn: false, isLoaded: true } };

    case KENNO_TENANT_ACTIONS.LOAD_BILLING_HISTORY:
      return {
        ...state,
        billingHistory: { ...state.billingHistory, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_BILLING_HISTORY_SUCCESS:
      payload.sort((a: BillingMonth, b: BillingMonth) => +(a.startDate < b.startDate) - +(a.startDate > b.startDate));

      const num = payload.length && payload[0].year * 12 + payload[0].month;

      return {
        ...state,
        billingHistory: {
          ...state.billingHistory,
          items: payload,
          rangeEnd: num,
          rangeStart: num - 2,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_BILLING_HISTORY_FAILURE:
      return {
        ...state,
        billingHistory: {
          ...state.billingHistory,
          items: [],
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.SET_BILLING_FILTER:
      return {
        ...state,
        billingHistory: { ...state.billingHistory, filter: payload },
      };
    case KENNO_TENANT_ACTIONS.SET_BILLING_RANGE_START:
      return {
        ...state,
        billingHistory: { ...state.billingHistory, rangeStart: payload },
      };
    case KENNO_TENANT_ACTIONS.SET_BILLING_RANGE_END:
      return {
        ...state,
        billingHistory: { ...state.billingHistory, rangeEnd: payload },
      };

    case KENNO_TENANT_ACTIONS.LOAD_CURRENT_BILL:
      return {
        ...state,
        currentBill: { ...state.currentBill, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_CURRENT_BILL_SUCCESS:
      if (!payload) {
        return {
          ...state,
          currentBill: {
            ...state.currentBill,
            loadState: LOAD_STATES.READY,
          },
        };
      }

      const dueDate = payload.dueDate ? new Date(payload.dueDate) : null;
      return {
        ...state,
        currentBill: {
          ...state.currentBill,
          totalRent: payload.totalRent,
          difference: payload.difference,
          debt: payload.debt,
          dueDate,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_CURRENT_BILL_FAILURE:
      return {
        ...state,
        currentBill: {
          ...state.currentBill,
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_CURRENT_TARGETS:
      return {
        ...state,
        currentTargets: { ...state.currentTargets, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_CURRENT_TARGETS_SUCCESS:
      return {
        ...state,
        currentTargets: {
          ...state.currentTargets,
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_CURRENT_TARGETS_FAILURE:
      return {
        ...state,
        currentTargets: {
          ...state.currentTargets,
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_DEBT:
      return {
        ...state,
        debt: { ...state.debt, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_DEBT_SUCCESS:
      return {
        ...state,
        debt: {
          ...state.debt,
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_DEBT_FAILURE:
      return {
        ...state,
        debt: {
          ...state.debt,
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_SECURITY_DEPOSIT:
      return {
        ...state,
        securityDeposit: { ...state.securityDeposit, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_SECURITY_DEPOSIT_SUCCESS:
      if (!payload) {
        return {
          ...state,
          securityDeposit: {
            ...state.securityDeposit,
            loadState: LOAD_STATES.READY,
          },
        };
      }

      const securityDepositDueDate = new Date(payload.dueDate);
      return {
        ...state,
        securityDeposit: {
          ...state.securityDeposit,
          data: { ...payload, dueDate: securityDepositDueDate },
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_SECURITY_DEPOSIT_FAILURE:
      return {
        ...state,
        securityDeposit: {
          ...state.securityDeposit,
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_CONTRACT:
      return {
        ...state,
        contract: { ...state.contract, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_CONTRACT_SUCCESS:
      const startDate = payload.startDate ? new Date(payload.startDate) : null;
      const endDate = payload.endDate ? new Date(payload.endDate) : null;
      return {
        ...state,
        contract: {
          data: {
            ...payload,
            startDate,
            endDate,
          },
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_CONTRACT_FAILURE:
      return {
        ...state,
        contract: {
          ...state.contract,
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_APARTMENT:
      return {
        ...state,
        apartment: { ...state.apartment, loadState: LOAD_STATES.BUSY },
      };

    case KENNO_TENANT_EVENTS.LOAD_APARTMENT_SUCCESS:
      return {
        ...state,
        apartment: {
          ...state.apartment,
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };

    case KENNO_TENANT_EVENTS.LOAD_APARTMENT_FAILURE:
      return {
        ...state,
        apartment: {
          ...state.apartment,
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_BUILDING:
      return {
        ...state,
        building: { ...state.building, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_BUILDING_SUCCESS:
      return {
        ...state,
        building: {
          ...state.building,
          data: {
            ...payload,
          },
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_BUILDING_FAILURE:
      return {
        ...state,
        building: {
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_DOCUMENTS:
      return {
        ...state,
        documents: { ...state.documents, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_DOCUMENTS_SUCCESS:
      return {
        ...state,
        documents: {
          ...state.documents,
          data: {
            ...payload,
          },
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_DOCUMENTS_FAILURE:
      return {
        ...state,
        documents: {
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_PUBLIC_NOTICES:
      return {
        ...state,
        publicNotices: { ...state.publicNotices, loadState: LOAD_STATES.BUSY },
      };

    case KENNO_TENANT_EVENTS.LOAD_PUBLIC_NOTICES_SUCCESS:
      return {
        ...state,
        publicNotices: {
          ...state.publicNotices,
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_PUBLIC_NOTICES_FAILURE:
      return {
        ...state,
        publicNotices: {
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_PUBLIC_EVENTS:
      return {
        ...state,
        publicEvents: { ...state.publicEvents, loadState: LOAD_STATES.BUSY },
      };

    case KENNO_TENANT_EVENTS.LOAD_PUBLIC_EVENTS_SUCCESS:
      return {
        ...state,
        publicEvents: {
          ...state.publicEvents,
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_PUBLIC_EVENTS_FAILURE:
      return {
        ...state,
        publicEvents: {
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_OWN_FORMS:
      return {
        ...state,
        ownForms: { ...state.ownForms, loadState: LOAD_STATES.BUSY },
      };

    case KENNO_TENANT_EVENTS.LOAD_OWN_FORMS_SUCCESS:
      return {
        ...state,
        ownForms: {
          ...state.ownForms,
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_OWN_FORMS_FAILURE:
      return {
        ...state,
        ownForms: {
          loadState: LOAD_STATES.ERROR,
        },
      };

    case KENNO_TENANT_ACTIONS.LOAD_TERMINATION_NOTICE:
      return {
        ...state,
        terminationNotice: { ...state.terminationNotice, loadState: LOAD_STATES.BUSY },
      };
    case KENNO_TENANT_EVENTS.LOAD_TERMINATION_NOTICE_SUCCESS:
      return {
        ...state,
        terminationNotice: {
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_TERMINATION_NOTICE_FAILURE:
      return {
        ...state,
        terminationNotice: {
          loadState: LOAD_STATES.ERROR,
        },
      };
    case KENNO_TENANT_ACTIONS.LOAD_KENNO_NOTIFICATIONS:
      return {
        ...state,
        notifications: { ...state.notifications, loadState: LOAD_STATES.BUSY },
      };

    case KENNO_TENANT_EVENTS.LOAD_KENNO_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        notifications: {
          data: payload,
          loadState: LOAD_STATES.READY,
        },
      };
    case KENNO_TENANT_EVENTS.LOAD_KENNO_NOTIFICATIONS_FAILURE:
      return {
        ...state,
        notifications: {
          loadState: LOAD_STATES.ERROR,
        },
      };

    default:
      console.log('Unknown event', type);
      return state;
  }
}
